import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';

import { countryFiltersAtom } from '../store/atoms/countryFiltersAtom';
import type { CountryFilters } from '../types/countryFilters';

/**
 * Hook para gerenciar os filtros (client-side) da listagem de Países.
 *
 * @returns Filtros atuais e funções para atualizar e resetar.
 *
 * @example
 * ```tsx
 * const { filters, updateFilters, resetFilters } = useCountryFilters();
 * updateFilters({ page: 2 });
 * ```
 */
export function useCountryFilters() {
  const [filters, setFilters] = useAtom(countryFiltersAtom);
  const resetFilters = useResetAtom(countryFiltersAtom);

  /**
   * Atualiza parcialmente os filtros. Reseta para a primeira página, exceto
   * quando o próprio `page` é fornecido no `partial` (navegação de páginas).
   */
  const updateFilters = (partial: Partial<CountryFilters>) => {
    setFilters(prev => {
      const resetPage = 'page' in partial ? {} : { page: 1 };
      return { ...prev, ...resetPage, ...partial };
    });
  };

  return { filters, updateFilters, resetFilters } as const;
}
